import { UseMutationResult, UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { pickDefined } from 'api/api.utils'
import { apiClient } from 'api/apiClient/apiClient'
import { OPT_JOBS_QUERY_KEY } from 'api/optJobs/optJobs.api'
import {
  OptimizationSnapshotDiff,
  OptimizationSnapshotResponseSchema,
  OptimizationTroubleshootLog,
  OptimizationTroubleshootLogCreate,
  OptimizationTroubleshootLogCreateSchema,
  OptimizationTroubleshootLogDeleteSchema,
  OptimizationTroubleshootLogSchema,
  OptimizationTroubleshootLogsResponse,
  OptimizationTroubleshootLogsResponseSchema,
  OptimizationTroubleshootLogUpdate,
  OptimizationTroubleshootLogUpdateSchema,
  OptJobReview,
  OptJobReviewSchema,
} from './optimizations.serializers'

const OPTIMIZATIONS_QUERY_KEY = 'optimizations'

export const optimizationsQueryKeys = {
  root: [OPTIMIZATIONS_QUERY_KEY] as const,
  review: (optJobId?: number, queueItemId?: number | null) =>
    [OPT_JOBS_QUERY_KEY, 'review', optJobId, queueItemId] as const,
  snapshotDiff: (
    previousOptJobId?: number,
    previousQueueItemId?: number | null,
    nextOptJobId?: number,
    nextQueueItemId?: number | null
  ) =>
    [
      OPTIMIZATIONS_QUERY_KEY,
      'snapshotDiff',
      previousOptJobId,
      previousQueueItemId,
      nextOptJobId,
      nextQueueItemId,
    ] as const,
  troubleshootLogs: (optJobId?: number, queueItemId?: number | null) =>
    [OPTIMIZATIONS_QUERY_KEY, 'troubleshootLogs', optJobId, queueItemId] as const,
  troubleshootLogsCreate: () => [OPTIMIZATIONS_QUERY_KEY, 'troubleshootLogs', 'create'] as const,
  troubleshootLogsUpdate: (logId: number) => [OPTIMIZATIONS_QUERY_KEY, 'troubleshootLogs', 'update', logId] as const,
  troubleshootLogsDelete: (logId: number) => [OPTIMIZATIONS_QUERY_KEY, 'troubleshootLogs', 'delete', logId] as const,
}

/*** Opt Job Review ***/

type OptJobReviewParams = {
  optJobId?: number
  queueItemId?: number | null
}

export function useOptJobReview({ optJobId, queueItemId }: OptJobReviewParams): UseQueryResult<OptJobReview> {
  return useQuery({
    queryKey: [OPT_JOBS_QUERY_KEY, 'review', optJobId, queueItemId],
    enabled: !!optJobId,
    queryFn: async () => {
      const params = pickDefined({ opt_job_id: optJobId, queue_item_id: queueItemId })
      const response = await apiClient(`optimizations/${optJobId}/review`, { params })
      return OptJobReviewSchema.parse(response)
    },
  })
}

/*** Snapshot Diff ***/

type OptimizationSnapshotDiffParams = {
  previousOptJobId?: number
  previousQueueItemId?: number | null
  nextOptJobId?: number
  nextQueueItemId?: number | null
}

export function useOptimizationSnapshotDiff({
  previousOptJobId,
  previousQueueItemId,
  nextOptJobId,
  nextQueueItemId,
}: OptimizationSnapshotDiffParams): UseQueryResult<OptimizationSnapshotDiff> {
  return useQuery({
    queryKey: optimizationsQueryKeys.snapshotDiff(previousOptJobId, previousQueueItemId, nextOptJobId, nextQueueItemId),
    enabled: (!!previousOptJobId || !!previousQueueItemId) && (!!nextOptJobId || !!nextQueueItemId),
    queryFn: async () => {
      const params = pickDefined({
        left_queue_item_id: previousQueueItemId,
        right_queue_item_id: nextQueueItemId,
        left_opt_job_id: previousOptJobId,
        right_opt_job_id: nextOptJobId,
      })
      const response = await apiClient(`optimizations/snapshots/compare`, { params })
      return OptimizationSnapshotResponseSchema.parse(response)
    },
  })
}

// *** Troubleshoot Logs **** //

type OptimizationTroubleshootLogsParams = {
  optJobId?: number
  queueItemId?: number | null
}

export function useOptimizationTroubleshootLogs({
  optJobId,
  queueItemId,
}: OptimizationTroubleshootLogsParams): UseQueryResult<OptimizationTroubleshootLogsResponse> {
  return useQuery({
    queryKey: optimizationsQueryKeys.troubleshootLogs(optJobId, queueItemId),
    enabled: !!optJobId,
    queryFn: async () => {
      const params = pickDefined({ opt_job_id: optJobId, queue_item_id: queueItemId })
      const response = await apiClient(`optimizations/troubleshoot/logs`, { params })
      return OptimizationTroubleshootLogsResponseSchema.parse(response)
    },
  })
}

export function useCreateOptimizationTroubleshootLog(): UseMutationResult<
  OptimizationTroubleshootLog,
  unknown,
  OptimizationTroubleshootLogCreate
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: optimizationsQueryKeys.troubleshootLogsCreate(),
    mutationFn: async (payload) => {
      const body = OptimizationTroubleshootLogCreateSchema.parse(payload)
      const response = await apiClient(`optimizations/troubleshoot/logs/create`, {
        method: 'POST',
        data: body,
      })
      return OptimizationTroubleshootLogSchema.parse(response)
    },
    onSuccess: (data) => {
      // Invalidate the specific troubleshoot logs list queries for the relevant opt_job/queue_item
      queryClient.invalidateQueries({
        queryKey: optimizationsQueryKeys.troubleshootLogs(data.opt_job, data.queue_item),
      })
    },
  })
}

export function useUpdateOptimizationTroubleshootLog(
  logId: number
): UseMutationResult<OptimizationTroubleshootLog, unknown, OptimizationTroubleshootLogUpdate> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: optimizationsQueryKeys.troubleshootLogsUpdate(logId),
    mutationFn: async (patch) => {
      const body = OptimizationTroubleshootLogUpdateSchema.parse(patch)
      const response = await apiClient(`optimizations/troubleshoot/logs/${logId}/update`, {
        method: 'PATCH',
        data: body,
      })
      return OptimizationTroubleshootLogSchema.parse(response)
    },
    onSuccess: (data) => {
      // Invalidate list + specific log update key
      queryClient.invalidateQueries({
        queryKey: optimizationsQueryKeys.troubleshootLogs(data.opt_job, data.queue_item),
      })
      queryClient.invalidateQueries({
        queryKey: optimizationsQueryKeys.troubleshootLogsUpdate(logId),
      })
    },
  })
}

export function useDeleteOptimizationTroubleshootLog(
  logId: number,
  paramsForInvalidateQueries: { optJobId: number; queueItemId?: number | null }
): UseMutationResult<void, unknown, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: optimizationsQueryKeys.troubleshootLogsDelete(logId),
    mutationFn: async () => {
      await apiClient(`optimizations/troubleshoot/logs/${logId}/delete`, { method: 'DELETE' })
      // Represent 204 via schema parse (will yield undefined)
      OptimizationTroubleshootLogDeleteSchema.parse(undefined)
    },
    onSuccess: () => {
      const { optJobId, queueItemId } = paramsForInvalidateQueries
      // Invalidate list
      queryClient.invalidateQueries({
        queryKey: optimizationsQueryKeys.troubleshootLogs(optJobId, queueItemId ?? null),
      })
    },
  })
}
